import { fetchTraceyUsage } from '../APIManager.js';

/**
 * Shared Tracey credit-balance state for both chat screens: the full-page
 * `/tracey` chat (TraceyPageChat.vue) and the in-platform chat drawer
 * (TraceyChatWindow.vue).
 *
 * Sprint and Ultra each have their own wallet, and both balances are shown
 * at once (not just whichever agent is currently selected) -- see
 * `creditsChips`. Each host loads both on mount and keeps them both fresh;
 * a stream's `balance` chunk carries its own `agent_version` (see
 * balance_snapshot() server-side), so a snapshot always updates the right
 * one even if the user switches agents mid-stream.
 *
 * `outOfCredits` and `creditsResetLabel` are COMPUTED from
 * creditsByAgent[traceyVersion] -- not stored/manually-synced data -- so
 * switching agents is correct instantly, without waiting on a refetch to
 * land. (An earlier version stored outOfCredits as data and only updated it
 * from applyCreditSnapshot(); switching agents faster than that refetch
 * resolved could leave it stuck on the previous agent's state -- e.g. Ultra
 * empty, switch to Sprint, composer stays blocked until the fetch caught
 * up.)
 *
 * The mixin owns the balance snapshots, formatted chip labels, the
 * low/empty flags per agent, and the out-of-credits block state. Each host
 * component wires up its own chip + composer notice and feeds results in
 * via applyCreditSnapshot() (stream `balance` chunk or the usage endpoint)
 * and handleCreditHttpError() (a 402/409 from the stream endpoint).
 *
 * Note on "out of credits": we only hard-block sending when the server
 * reports `enforced: true` AND the balance is spent. While metering is in
 * measure-only mode (CREDITS_ENFORCED=False) the chip still turns red at zero,
 * but queries are not blocked — matching the backend, which keeps serving.
 */
export default {
  data() {
    return {
      creditsByAgent: { ultra: null, sprint: null }, // last balance snapshot per agent
      showCreditsChip: true,
    };
  },
  computed: {
    // The currently selected agent's snapshot -- what gates the composer.
    credits() {
      return this.creditsByAgent[this.traceyVersion] || null;
    },
    creditsBalance() {
      return this.credits ? Number(this.credits.balance_credits) || 0 : null;
    },
    creditsAllowance() {
      return this.credits ? Number(this.credits.allowance_credits) || 0 : null;
    },
    outOfCredits() {
      return !!(this.credits && this.credits.enforced && this.creditsBalance <= 0);
    },
    creditsResetAt() {
      return (this.credits && this.credits.period_end) || null;
    },
    creditsResetLabel() {
      if (!this.creditsResetAt) return '';
      const d = new Date(this.creditsResetAt);
      if (isNaN(d)) return '';
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    },
    // One entry per agent, for rendering both balance chips side by side.
    creditsChips() {
      return ['ultra', 'sprint'].map((agent) => {
        const snap = this.creditsByAgent[agent];
        const bal = snap ? Number(snap.balance_credits) || 0 : null;
        const allow = snap ? Number(snap.allowance_credits) || 0 : null;
        let label = '';
        if (bal !== null) {
          if (bal <= 0) label = '0';
          else if (bal < 1) label = '<1';
          else label = String(Math.round(bal));
        }
        const empty = bal !== null && bal <= 0;
        const low = bal !== null && !!allow && bal <= allow * 0.1;
        const parts = bal !== null ? [`${label} Tracey ${agent === 'ultra' ? 'Ultra' : 'Sprint'} credits remaining`] : [];
        if (snap && snap.period_end) {
          const d = new Date(snap.period_end);
          if (!isNaN(d)) parts.push(`Resets ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`);
        }
        if (snap && snap.pending_runs) parts.push('Calculating last query…');
        return {
          key: agent,
          label,
          balance: bal,
          loaded: snap !== null,
          empty,
          low,
          tooltip: parts.join(' · '),
        };
      });
    },
  },
  methods: {
    async loadCredits(version) {
      const agent = version || this.traceyVersion;
      try {
        const snap = await fetchTraceyUsage(agent);
        if (snap) this.applyCreditSnapshot(snap, agent);
      } catch (e) {
        // Non-fatal: that agent's chip just stays hidden if usage can't be read.
        console.warn(`Could not load Tracey ${agent} usage:`, e);
      }
    },
    loadAllCredits() {
      return Promise.all([this.loadCredits('ultra'), this.loadCredits('sprint')]);
    },
    applyCreditSnapshot(snap, version) {
      if (!snap) return;
      const agent = snap.agent_version || version || this.traceyVersion;
      this.creditsByAgent = { ...this.creditsByAgent, [agent]: snap };
    },
    /**
     * Handle a non-OK response from the stream endpoint. Returns the kind of
     * credit error handled ('credits_exhausted' | 'query_already_running'), or
     * null if this wasn't a credit/lock status so the caller falls through to
     * its generic error path.
     */
    handleCreditHttpError(status, body) {
      if (status !== 402 && status !== 409) return null;
      let parsed = {};
      try {
        parsed = JSON.parse(body) || {};
      } catch (e) {
        /* non-JSON body */
      }
      if (status === 402) {
        // Mark the agent that was actually queried (the current
        // traceyVersion, at the moment the 402 came back) as depleted in
        // the cache immediately, so outOfCredits reflects it without
        // waiting on the refetch below to land.
        const agent = this.traceyVersion;
        const prev = this.creditsByAgent[agent] || {};
        this.creditsByAgent = {
          ...this.creditsByAgent,
          [agent]: {
            ...prev,
            balance_credits: 0,
            enforced: true,
            period_end: parsed.resets_at || prev.period_end,
          },
        };
        this.loadCredits(agent);
        return 'credits_exhausted';
      }
      return 'query_already_running';
    },
  },
};
